/** Last undo surface from a main editing view (editor / overview / font info). */
let lastMainUndoContext = null;
/** Test helper — clears sticky main-view undo context. */
export function resetUndoRedoContextStickyState() {
    lastMainUndoContext = null;
}
function isViewFocused(viewId) {
    return (document.querySelector(viewId)?.classList.contains('focused') ?? false);
}
function isFontInfoViewFocused() {
    return isViewFocused('#view-fontinfo');
}
function isOverviewViewFocused() {
    return isViewFocused('#view-overview');
}
function isEditorViewFocused() {
    return isViewFocused('#view-editor');
}
/** Scripts, Konsole, and Assistant own the automation undo surface. */
export function isAutomationUndoViewFocused() {
    return (isViewFocused('#view-scripts') ||
        isViewFocused('#view-console') ||
        isViewFocused('#view-assistant'));
}
function isFontInfoFeaturesTabVisible() {
    const featuresTab = document.getElementById('fontinfo-features-content');
    if (!featuresTab) {
        return false;
    }
    return featuresTab.style.display !== 'none';
}
function rememberMainUndoContext(context) {
    lastMainUndoContext = context;
    return context;
}
function buildCanvasContext(rootGlyphName, undoGlyphName, undoLayerId) {
    return {
        rootGlyphName,
        undoGlyphName,
        undoLayerId,
        historyTargetKey: null,
        surface: 'canvas'
    };
}
function buildFontContext(rootGlyphName) {
    return {
        rootGlyphName,
        undoGlyphName: undefined,
        undoLayerId: null,
        historyTargetKey: null,
        surface: 'font'
    };
}
function buildOverviewContext(rootGlyphName) {
    return {
        rootGlyphName,
        undoGlyphName: undefined,
        undoLayerId: null,
        historyTargetKey: null,
        surface: 'overview'
    };
}
function buildFeatureContext(rootGlyphName, historyTargetKey) {
    return {
        rootGlyphName,
        undoGlyphName: undefined,
        undoLayerId: null,
        historyTargetKey,
        surface: 'feature'
    };
}
function buildAutomationContext(rootGlyphName) {
    return {
        rootGlyphName,
        undoGlyphName: undefined,
        undoLayerId: null,
        historyTargetKey: null,
        surface: 'automation'
    };
}
/**
 * Undo / History reachability follows the focused editing surface.
 *
 * Main surfaces: glyph editor, overview, Font Info.
 * Automation surface: Scripts, Konsole, Assistant (Python / AI font edits).
 * Auxiliary panels such as History do not switch the surface — they keep the
 * last main-view context (not the automation surface).
 */
export function getUndoRedoContext() {
    const oe = window.glyphCanvas?.outlineEditor;
    const parsedStack = oe?.active ? oe.parseGlyphStack() : [];
    const currentGlyphName = oe?.currentGlyphName ?? window.glyphCanvas?.getCurrentGlyphName?.();
    const rootGlyphName = parsedStack[0]?.glyphName ?? currentGlyphName;
    const fallbackUndoGlyphName = parsedStack[parsedStack.length - 1]?.glyphName ?? currentGlyphName;
    const fallbackUndoLayerId = oe?.selectedLayerId ?? null;
    const fontInfoFocused = isFontInfoViewFocused();
    const overviewFocused = isOverviewViewFocused();
    const editorFocused = isEditorViewFocused();
    const automationFocused = isAutomationUndoViewFocused();
    const featuresTabVisible = isFontInfoFeaturesTabVisible();
    const mainViewFocused = fontInfoFocused || overviewFocused || editorFocused;
    if (fontInfoFocused && featuresTabVisible) {
        const featureTarget = window.fontInfoManager?.getHistoryScopeTarget?.() ?? null;
        if (featureTarget?.key) {
            return rememberMainUndoContext(buildFeatureContext(rootGlyphName, featureTarget.key));
        }
        return rememberMainUndoContext(buildFontContext(rootGlyphName));
    }
    if (fontInfoFocused) {
        return rememberMainUndoContext(buildFontContext(rootGlyphName));
    }
    if (overviewFocused) {
        return rememberMainUndoContext(buildOverviewContext(rootGlyphName));
    }
    if (editorFocused &&
        oe?.active &&
        fallbackUndoGlyphName &&
        fallbackUndoLayerId) {
        return rememberMainUndoContext(buildCanvasContext(rootGlyphName, fallbackUndoGlyphName, fallbackUndoLayerId));
    }
    if (editorFocused) {
        // Text mode (or editor without a selected layer): Editing View /
        // canvas surface. Kerning and other editor-owned font edits join
        // via undoSurfaceAffinity; layer-origin edits for the caret glyph
        // remain reachable without inventing a fake layer id.
        return rememberMainUndoContext(buildCanvasContext(rootGlyphName, fallbackUndoGlyphName, fallbackUndoLayerId));
    }
    // Scripts / Konsole / Assistant: undo Python- and Assistant-sourced edits
    // regardless of derived font/glyph/layer scope. Do not sticky this as the
    // main-view context — History keeps the last Editor/Overview/Font Info.
    if (automationFocused) {
        return buildAutomationContext(rootGlyphName);
    }
    // History and other auxiliary panels: keep last main editing surface.
    if (!mainViewFocused && lastMainUndoContext) {
        return lastMainUndoContext;
    }
    // Cold start with no main view focused yet.
    return buildFontContext(rootGlyphName);
}
window.getUndoRedoContext = getUndoRedoContext;
